// src\app\services\user\user.service.ts
import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable, Subject } from "rxjs";
import { AppConstants } from 'src/app/constants/app-constants';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Company, CompanyMapper } from "src/app/model/Company";
import { User, UserInterface } from "src/app/model/User";
import { throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { AuthService } from "../auth/auth.service";
import { Router } from "@angular/router";
import { 
  TOKEN_USER_ENDPOINT, 
  USERS_TOS_VERIFY_ENDPOINT, 
  USER_ACCEPT_TOS_ENDPOINT, 
  USER_CHANGE_PASSWORD_ENDPOINT, 
  USER_UPDATE_INFO_ENDPOINT, 
  PASSWORD_RESET_REQUEST_ENDPOINT 
} from 'src/app/constants/api-routes';
import { DialogService } from "../dialog/dialog.service";

/** Interface needed to provide to accept tos method */
export interface AcceptTosInterface {
  tosRecord: {
    id: number;
    companyID: string;
    userID: string;
    tosVersion: string;
    tosAccepted: boolean;
    tosAcceptedDate: string;
  };
  tosVersion: string;
}

/** Interface returned on tos acceptance check */
export interface VerifyTosInterface {
  companyID: number;
  tosVersion: string;
  accepted: boolean;
}

/**
 * UserService handles:
 * - obtaining session tokens
 * - storing session/user information
 * - toggling between different companies associated with a user
 * 
 * @todo Add functionality to handle multiple companies. Company switching should be handled in this class
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  /** The current user */
  private _userSubject = new BehaviorSubject<User | null>(null);
  public user$ = this._userSubject.asObservable();

  /** The company of the current user */
  private _companySubject = new BehaviorSubject<Company | null>(null);
  public company$ = this._companySubject.asObservable();

  /** The email of the current user */
  private _emailSubject = new BehaviorSubject<string | null>(null);
  public email$ = this._emailSubject.asObservable();

  /** TOS acceptance of the current user */
  private _tosAcceptedSubject = new BehaviorSubject<boolean>(false);
  public tosAccepted$ = this._tosAcceptedSubject.asObservable();

  /** Loading status of the user object */
  private _loadingSubject = new BehaviorSubject<boolean>(true);
  public loading$ = this._loadingSubject.asObservable();

  /**
   * Default constructor for the user service which loads the token
   */
  constructor(
    private _authService: AuthService,
    private _http: HttpClient,
    private _router: Router,
    private _dialogService: DialogService,
  ) {
    // When the auth service loads, load the user
    this._authService.loading$.subscribe(loading => {
      if (!loading) this._loadUser();
    });

    // Subscribe to authentication changes
    this._authService.isAuthenticated$.subscribe((isAuthenticated: boolean) => {
      if (this._authService.loading) return;

      // Depending on authenticated status, either load or clear user
      isAuthenticated ? this._loadUser() : this._clearUser();
    });
  }

  /**
   * Helper method to load user information from the backend
   */
  private _loadUser(): void {
    // If we've already loaded user object return
    if (this.user !== null) return;

    this._loadingSubject.next(true);

    // Get token
    const token = this._authService.token;
    if (token == undefined || token.trim() === "") {
      this._loadingSubject.next(false);
      return;
    }

    // Form backend calls
    const userCall = this._http.post<UserInterface>(TOKEN_USER_ENDPOINT, token);
    const tosCall = this._http.get<VerifyTosInterface>(USERS_TOS_VERIFY_ENDPOINT);

    forkJoin([
      userCall,
      tosCall,
    ])
    .pipe(finalize(() => this._loadingSubject.next(false))) // Update loading state on completion
    .subscribe({
      next: ([user, tos]) => {
        // NOTE: deactivation checks on authentication are handled in backend endpoint. This handles refresh cases

        // Handle user status
        const userStatus = user.status;
        if (userStatus === 'terminated' || userStatus === 'deactivated') {
          this._authService.logout();
          this._router.navigate([AppConstants.MENU_LOGIN], { queryParams: {'status': userStatus }});
          return;
        }

        // Handle company status
        const companyStatus = user.companies[0].companyStatus;
        if (companyStatus === 'suspended') {
          this._dialogService.openSuspensionDialog();
        } else if (companyStatus !== 'active') {
          throw new Error("Company status (" + companyStatus + ") is unknown or not handled");
        }

        // Update user subjects
        this._companySubject.next(CompanyMapper.toModel(user.companies[0]));
        this._emailSubject.next(user.email);
        this._userSubject.next(new User(user));

        // Update tos subject
        this._tosAcceptedSubject.next(tos.accepted);

        // Navigate to default nav path
        this._router.navigate([this.defaultNavPath]);
      },
      error: (error: HttpErrorResponse) => {
        console.error("[USER]: Failed to load user or TOS info", error);
      }
    });
  }

  /**
   * Helper method to clear user information
   */
  private _clearUser(): void {
    console.log("[USER]: Clearing user information");
    
    this._userSubject.next(null);
    this._companySubject.next(null);
    this._emailSubject.next(null);
  }

  /**
   * Accept TOS for the user by sending a request to the backend. An observable is returned
   * with status of acceptance (true = accepted, false = not accepted)
   * 
   * @param user
   * @returns 
   */
  public acceptTOS(user: User): Observable<boolean> {
    // Get company id and user id
    const cifno = user.companies[0].companyId;
    const id = user.id;

    // Call the backend endpoint to accept TOS
    return this._http.post<AcceptTosInterface>(USER_ACCEPT_TOS_ENDPOINT(cifno, id), {}).pipe(
      map((response: AcceptTosInterface) => {
        const accepted = response.tosRecord.tosAccepted;
        this._tosAcceptedSubject.next(accepted);
        return accepted;
      }),
      catchError((error: any) => {
        console.error("[USER]: could not accept TOS", error);
        return throwError(() => error);
      }),
    );
  }

  /**
 * Change the current user's password.
 * 
 * @param oldPassword The user's current password.
 * @param newPassword The new password to set.
 * @returns An Observable of the updated User object.
 */
  public changePassword(oldPassword: string, newPassword: string): Observable<User> {
    // Retrieve the current user and company details.
    const user = this._userSubject.value;
    const company = this._companySubject.value;

    // Ensure both the user and the company are loaded.
    if (!user || !company) {
      return throwError(() => new Error("User or company not found"));
    }

    // Construct the backend URL for changing the password.
    const url = USER_CHANGE_PASSWORD_ENDPOINT(company.companyId, user.id);

    // Create the payload using the expected DTO keys.
    const dto = {
      oldPassword: oldPassword,
      newPassword: newPassword
    };

    // Send the PUT request and return the resulting Observable.
    return this._http.put<User>(url, dto).pipe(
      catchError((error) => {
        console.error('[USER]: failed to change password', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update the current user's basic information.
   *
   * @param firstName New first name.
   * @param lastName New last name.
   * @param email New email.
   * @param phoneNumber New phone number.
   * @returns Observable of the updated User object.
   */
  public updateUserInfo(firstName: string, lastName: string, phoneNumber: string): Observable<User> {
    // Retrieve the current user and company details.
    const user = this._userSubject.value;
    const company = this._companySubject.value;

    if (!user || !company) {
      return throwError(() => new Error("User or company not found"));
    }

    // Build the DTO matching the backend's UpdateUserInfoDTO
    const dto = { firstName, lastName, phoneNumber };

    // Call endpoint
    const url = USER_UPDATE_INFO_ENDPOINT(company.companyId, user.id);
    return this._http.put<User>(url, dto).pipe(
      catchError((error) => {
        console.error('Error updating user info:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Send a password reset request
   * 
   * @param email The email of the user
   * @returns Observable<void>
   */
  requestPasswordReset(email: string): Observable<void> {
    console.log("[USER]: reset password request");

    // Create a subject to return to the caller
    const subject = new Subject<void>();

    // Attempt to send the password reset request
    return this._http.post<void>(PASSWORD_RESET_REQUEST_ENDPOINT, email);
  }

  // Inside UserService
  public updateLocalUser(updatedUser: User): void {
    this._userSubject.next(updatedUser);
  }

  // Calls the GET endpoint for verifying TOS acceptance for the user's companies.
  public verifyTOS(): Observable<VerifyTosInterface> {
    return this._http.get<VerifyTosInterface>(USERS_TOS_VERIFY_ENDPOINT)
  }

  /** Getters */
  get user(): User | null {
    return this._userSubject.value;
  }

  get company(): Company | null {
    return this._companySubject.value;
  }

  get tosAccepted(): boolean {
    return this._tosAcceptedSubject.value;
  }

  get loading(): boolean {
    return this._loadingSubject.value;
  }

  /**
   * Get the default path to navigate to depending on the user's role
   * 
   * @note this should be changed to a different service if the user can have more than one company
   * 
   * @returns The default path to navigate to
   */
  get defaultNavPath(): string {
    // If the user doesn't exist, return to login
    if (this.user == undefined) return AppConstants.MENU_LOGIN;

    // Get the user's role
    // TODO: this assumes that users only have one associated company and may change in the future
    const role = this.user.companies[0].companyType;

    // Return associated url path
    if (role === "SUPPLIER") {
      return AppConstants.MENU_APPLICATIONS_INVITES;
    } else {
      return AppConstants.MENU_HOME;
    }
  }

  /**
   * Get the user's role 
   * 
   * @note this should be changed to a different service if the user can have more than one company
   * 
   * @returns The user's role, null if the user doesn't exist
   */
  get userRole(): 'BORROWER' | 'SUPPLIER' | null {
    if (this.user == undefined) return null;

    return this.user.companies[0].companyType;
  }
}